import { useMemo } from "react";

import dayjs from "dayjs";
import type { Options as HighchartsOptions } from "highcharts";

import { shortMonthAndShortYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import DurationServiceSingleton from "app/services/DurationService";

import { POP_CURRENT_COLOR, POP_PREVIOUS_COLOR } from "./constants";
import {
    formatValue,
    renderComparePopTooltipContent,
    renderPopTooltipContent,
    renderSingleTooltipContent,
} from "./conversionRatesTooltips";
import { createParams, createPopParams, getCompareValue, getPointValue, getSeriesColor } from "./conversionRatesUtils";
import type { ConversionRatesProps, TooltipPoint } from "./types";
import { useConversionRateData, useConversionRateDataPOP } from "./useConversionRateData";

export function useConversionRatesChart({
    params,
    meta,
    chosenSites,
    getSiteColor,
    conversionRateAvailabilityState,
    hiddenSites,
}: ConversionRatesProps & { hiddenSites?: ReadonlySet<string> }) {
    const shouldFetchData =
        conversionRateAvailabilityState === "available" || conversionRateAvailabilityState === undefined;
    const singleApiParams = useMemo(() => createParams(params), [params]);
    const popApiParams = useMemo(() => createPopParams(params), [params]);
    const singleQuery = useConversionRateData(singleApiParams, { enabled: shouldFetchData && !meta.isPOP });
    const popQuery = useConversionRateDataPOP(popApiParams, { enabled: shouldFetchData && meta.isPOP });

    const query = meta.isPOP ? popQuery : singleQuery;
    const data = query.data ?? [];

    const popPeriodLabels = useMemo(() => {
        const durationData = DurationServiceSingleton.getDurationData(params.duration, params.comparedDuration);
        const widgetDuration = durationData?.forWidget;

        if (Array.isArray(widgetDuration) && widgetDuration.length >= 2) {
            return {
                current: String(widgetDuration[0]),
                previous: String(widgetDuration[1]),
            };
        }

        return {
            current: "Current period",
            previous: "Previous period",
        };
    }, [params.duration, params.comparedDuration]);

    const { compareDateLookup, tooltipChangeLookup, compareValueLookup } = useMemo(() => {
        const dates: Record<number, string> = {};
        const changes: Record<string, number> = {};
        const compareValues: Record<string, number | null> = {};

        data.forEach((item) => {
            item.data.forEach((point) => {
                const timestamp = dayjs.utc(point.date).valueOf();
                if (point.compareDate) {
                    dates[timestamp] = point.compareDate;
                }
                const changeValue = "change" in point ? Number(point.change ?? 0) : 0;
                changes[`${item.site}-current:${timestamp}`] = changeValue;
                compareValues[`${item.site}-current:${timestamp}`] = point.compareValue ?? null;
            });
        });

        return { compareDateLookup: dates, tooltipChangeLookup: changes, compareValueLookup: compareValues };
    }, [data]);

    const siteAverages = useMemo(() => {
        const averages: Record<string, number> = {};
        data.forEach((item) => {
            if (item.averageVisits != null) {
                averages[item.site] = Number(item.averageVisits);
            }
        });
        return averages;
    }, [data]);

    const isSinglePop = meta.isPOP && meta.isSingleMode !== false;
    const isComparePop = meta.isPOP && meta.isSingleMode === false;

    const graphSeries = useMemo(() => {
        const chosenSitesSet = chosenSites?.length ? new Set(chosenSites) : null;
        const filteredSites = data.filter((item) => !chosenSitesSet || chosenSitesSet.has(item.site));
        const sitesToRender = filteredSites.length ? filteredSites : data;

        // Compare+POP and non-POP: one line per domain with domain colors
        if (!isSinglePop) {
            return sitesToRender.map((item, index) => ({
                name: item.site,
                id: `${item.site}-current`,
                visible: !hiddenSites?.has(item.site),
                color: getSeriesColor(item.site, index, getSiteColor),
                data: item.data.map((point) => [dayjs.utc(String(point.date ?? "")).valueOf(), getPointValue(point)]),
            }));
        }

        // Single+POP: two lines per site (current + previous)
        return sitesToRender.flatMap((item) => {
            const timestamps = item.data.map((point) => dayjs.utc(String(point.date ?? "")).valueOf());

            return [
                {
                    name: popPeriodLabels.current,
                    id: `${item.site}-current`,
                    visible: true,
                    color: POP_CURRENT_COLOR,
                    data: item.data.map((point, i) => [timestamps[i], getPointValue(point)]),
                },
                {
                    name: popPeriodLabels.previous,
                    id: `${item.site}-previous`,
                    visible: true,
                    color: POP_PREVIOUS_COLOR,
                    data: item.data.map((point, i) => [timestamps[i], getCompareValue(point)]),
                },
            ];
        });
    }, [data, chosenSites, isSinglePop, popPeriodLabels, getSiteColor, hiddenSites]);

    const hasData = graphSeries.some((series) => series.data.some((point) => point[1] !== null));

    const popLegendItems = useMemo(() => {
        if (!isSinglePop) {
            return [];
        }

        return [
            {
                name: popPeriodLabels.current,
                color: POP_CURRENT_COLOR,
                transparentColor: colorsPalettes.blue[200],
            },
            {
                name: popPeriodLabels.previous,
                color: POP_PREVIOUS_COLOR,
                transparentColor: colorsPalettes.orange[200],
            },
        ];
    }, [isSinglePop, popPeriodLabels]);

    const graphConfig = useMemo<HighchartsOptions>(
        () => ({
            chart: {
                height: 320,
            },
            xAxis: {
                type: "datetime",
                labels: {
                    formatter({ value }) {
                        return dayjs.utc(value).format(shortMonthAndShortYear);
                    },
                },
            },
            yAxis: {
                labels: {
                    formatter({ value }) {
                        return formatValue(Number(value));
                    },
                },
            },
            tooltip: {
                shared: true,
                outside: true,
                useHTML: true,
                formatter() {
                    const timestamp = Number(this.x);
                    const points = (this.points || []) as TooltipPoint[];

                    if (isSinglePop) {
                        return renderPopTooltipContent(timestamp, points, {
                            tooltipChangeLookup,
                            compareDateLookup,
                            popPeriodLabels,
                            webSource: params.webSource ?? "",
                        });
                    }

                    if (isComparePop) {
                        return renderComparePopTooltipContent(timestamp, points, {
                            tooltipChangeLookup,
                            compareValueLookup,
                            compareDateLookup,
                        });
                    }

                    return renderSingleTooltipContent(timestamp, points, tooltipChangeLookup);
                },
            },
        }),
        [
            isSinglePop,
            isComparePop,
            tooltipChangeLookup,
            compareValueLookup,
            compareDateLookup,
            popPeriodLabels,
            params.webSource,
        ],
    );

    return {
        query,
        graphSeries,
        graphConfig,
        hasData,
        popLegendItems,
        siteAverages,
        emptyStateType: query.emptyStateType,
    };
}
